import { AuthEnums } from "models/enums";
import { DimensionData } from "models/propTypes/dimensionPropTypes";
import { Preset, SelectionItem } from "models/utilTypes/selectionTypes";
import { ServiceConstants } from "utils/constants";
import { ServiceHelper, MathHelper, LocalizationHelperInstance } from "utils/helpers";
import useAPIClientHelper from "./useAPIClientHelper";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

function useDimensionService() {
  const { apiClientInstance } = useAPIClientHelper();
  const getAllDimensionsByType = (typeId: string) => {
    return ServiceHelper.getAllPages((page) =>
      apiClientInstance(AuthEnums.AuthType.Company).DimensionClient.Get({
        ...ServiceConstants.DefaultFilter,
        page: page,
        typeId: typeId,
      })
    );
  };

  const getInvoiceDimensionTypes = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DimensionTypesClient.GetDimensionTypesForInvoice()
      .then((res) => res.data);
  };

  const getPresets = (presetType: WintSuperkollAPIModel.DimensionPresetType, dimensionType: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DimensionPresetClient.Get({
        presetType: presetType,
        dimensionType: dimensionType,
      } as WintSuperkollAPIModel.DimensionPresetFilter)
      .then((res) => {
        return res.data;
      });
  };

  const savePreset = (preset: Preset, presetType: WintSuperkollAPIModel.DimensionPresetType, dimensionType: string) => {
    const percentages = preset.presetRows.map((item) => ({
      dimensionId: item.id,
      percentage: item.percentage,
    }));
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DimensionPresetClient.Post({
      type: presetType,
      dimensionType: dimensionType,
      name: preset.text,
      percentages: percentages,
    } as WintSuperkollAPIModel.DimensionPreset);
  };

  const convertDimensionToSelectionItems = (
    dimensions: WintSuperkollAPIModel.Dimension[],
    amount: number,
    currency?: string
  ): SelectionItem[] => {
    return dimensions.map((item) => convertDimensionToSelectionItem(item, amount, currency));
  };

  const convertDimensionToSelectionItem = (
    dimension: WintSuperkollAPIModel.Dimension,
    amount: number,
    currency?: string,
    dimensionData?: WintSuperkollAPIModel.DimensionData,
    alwaysDisplayAmounts?: boolean,
    currentData?: DimensionData
  ): SelectionItem<DimensionData> => {
    return {
      id: dimension.id ?? "",
      value: dimension.name ?? "",
      data: dimensionData
        ? {
            amount: dimensionData && amount ? dimensionData.amount : 0,
            percentage:
              dimensionData && amount
                ? MathHelper.toFixedNoRound((dimensionData.amount / amount) * 100, 2)
                : !amount
                  ? (currentData?.percentage ?? 100)
                  : 0,
            fraction:
              dimensionData && amount
                ? [dimensionData.amount, amount]
                : !amount
                  ? (currentData?.fraction ?? [1, 1])
                  : [0, 1],
            dimensionId: dimension.id ?? "",
          }
        : ({} as DimensionData),
      selectedSubText:
        alwaysDisplayAmounts || (dimensionData && Math.abs(dimensionData.amount) < Math.abs(amount))
          ? LocalizationHelperInstance.getFormattedCurrency(dimensionData?.amount ?? 0, currency, 2, 2)
          : "",
      description: dimension.shortName,
    };
  };

  const createDimensionItem = (dimension: WintSuperkollAPIModel.DimensionPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DimensionClient.Post(dimension);
  };
  const updateDimensionItem = (id: string, dimension: WintSuperkollAPIModel.DimensionPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DimensionClient.Put(id, dimension);
  };
  const deleteDimensionItem = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DimensionClient.Delete(id);
  };
  return {
    getAllDimensionsByType,
    getInvoiceDimensionTypes,
    getPresets,
    savePreset,
    convertDimensionToSelectionItems,
    convertDimensionToSelectionItem,
    createDimensionItem,
    updateDimensionItem,
    deleteDimensionItem,
  };
}

export default useDimensionService;
